import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Wallet, Search, X, Check, AlertTriangle, Eye, ChevronLeft, Printer } from 'lucide-react';
import { overlayClose } from '../lib/overlayClose';
import { printHtmlSilently } from '../lib/silentPrint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_METHODS = [
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'pix', label: 'PIX Loja' },
  { value: 'pix_maquininha', label: 'PIX na Maquininha' },
  { value: 'credito', label: 'Crédito' },
  { value: 'debito', label: 'Débito' },
  { value: 'voucher', label: 'Voucher Alimentação' },
  { value: 'cheque', label: 'Cheque' },
];

// Imprime comprovante 80mm de recebimento
const printReceiptSlip = ({ customerName, description, saleNumber, paymentMethod, amountReceived, totalPaid, accountTotal, outstandingAfter, customerBalance, dueDate, interestPaid, principalPaid, operatorName, payments }) => {
  const fmt = (n) => Number(n || 0).toFixed(2).replace('.', ',');
  const now = new Date().toLocaleString('pt-BR');
  const methodLabel = PAYMENT_METHODS.find(p => p.value === paymentMethod)?.label || paymentMethod;
  const isFullyPaid = outstandingAfter < 0.01;
  const hasCustomerBalance = customerBalance !== undefined && customerBalance !== null;
  const historyRows = (payments && payments.length > 0)
    ? payments.map((p) => {
        const ml = PAYMENT_METHODS.find(m => m.value === p.payment_method)?.label || p.payment_method || '—';
        const dt = p.received_at ? new Date(p.received_at).toLocaleDateString('pt-BR') : '—';
        const jr = (p.interest_paid && p.interest_paid > 0) ? ` (juros ${fmt(p.interest_paid)})` : '';
        return `<div class="row" style="font-size:10px"><span>${dt} · ${ml}${jr}</span><span>R$ ${fmt(p.amount)}</span></div>`;
      }).join('')
    : '';
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Recebimento</title>
    <style>
      @page { size: 80mm auto; margin: 4mm; }
      body { font-family: 'Courier New', monospace; font-size: 11px; width: 72mm; }
      h1 { font-size: 14px; text-align: center; margin: 0 0 4px; }
      .hr { border-top: 1px dashed #000; margin: 4px 0; }
      .row { display: flex; justify-content: space-between; margin: 2px 0; }
      .big { font-size: 14px; font-weight: bold; }
      .warn { font-size: 13px; font-weight: bold; text-align: center; padding: 4px; border: 1px solid #000; margin-top: 4px; }
      .info { font-size: 13px; font-weight: bold; text-align: center; padding: 4px; border: 1px dashed #000; margin-top: 4px; }
      .center { text-align: center; }
    </style></head><body>
    <h1>RECEBIMENTO DE CREDIÁRIO</h1>
    <div class="center">Pagamento de Venda a Prazo</div>
    <div class="hr"></div>
    <div class="row"><span>Cliente:</span><strong>${customerName || '—'}</strong></div>
    ${saleNumber ? `<div class="row"><span>Venda nº:</span><span>#${saleNumber}</span></div>` : ''}
    <div class="row"><span>Conta:</span><span style="text-align:right">${description || '—'}</span></div>
    ${dueDate ? `<div class="row"><span>Vencimento:</span><span>${new Date(dueDate).toLocaleDateString('pt-BR')}</span></div>` : ''}
    <div class="hr"></div>
    <div class="row big"><span>VALOR PAGO AGORA</span><span>R$ ${fmt(amountReceived)}</span></div>
    <div class="row"><span>Forma:</span><strong>${methodLabel}</strong></div>
    ${interestPaid > 0 ? `<div class="row"><span>Juros:</span><span>R$ ${fmt(interestPaid)}</span></div>` : ''}
    ${accountTotal ? `<div class="row"><span>Valor total da conta:</span><span>R$ ${fmt(accountTotal)}</span></div>` : ''}
    ${(totalPaid !== undefined && totalPaid !== null) ? `<div class="row"><span>Total já pago (esta conta):</span><span>R$ ${fmt(totalPaid)}</span></div>` : ''}
    <div class="hr"></div>
    ${isFullyPaid
      ? `<div class="warn">✓ ESTA CONTA FOI QUITADA</div>`
      : `<div class="warn">SALDO DEVEDOR (esta conta): R$ ${fmt(outstandingAfter)}</div>`
    }
    ${hasCustomerBalance
      ? (Number(customerBalance) < 0.01
          ? `<div class="info">SALDO TOTAL DO CLIENTE: R$ 0,00 (sem dívidas)</div>`
          : `<div class="info">SALDO TOTAL DO CLIENTE: R$ ${fmt(customerBalance)}</div>`)
      : ''
    }
    ${historyRows ? `
    <div class="hr"></div>
    <div style="font-weight:bold;font-size:11px;margin-bottom:2px">PARCELAS PAGAS (${payments.length})</div>
    ${historyRows}
    <div class="row" style="font-weight:bold;border-top:1px solid #000;margin-top:2px;padding-top:2px"><span>Total pago (esta conta):</span><span>R$ ${fmt(totalPaid)}</span></div>
    ` : ''}
    <div class="hr"></div>
    <div class="row"><span>Operador:</span><span>${operatorName || '—'}</span></div>
    <div class="row"><span>Emitido em:</span><span>${now}</span></div>
    <div class="center" style="font-size: 9px; margin-top: 6px">Smart Flow · Comprovante para o cliente</div>
  </body></html>`;
  printHtmlSilently(html);
};

// Comprovante ÚNICO 80mm para várias notas quitadas no mesmo atendimento
const printBulkReceiptSlip = ({ customerName, paymentMethod, notes, customerBalance, operatorName }) => {
  const fmt = (n) => Number(n || 0).toFixed(2).replace('.', ',');
  const now = new Date().toLocaleString('pt-BR');
  const methodLabel = PAYMENT_METHODS.find(p => p.value === paymentMethod)?.label || paymentMethod || '—';
  const total = notes.reduce((s, n) => s + Number(n.amount || 0), 0);
  const totalInterest = notes.reduce((s, n) => s + Number(n.interest || 0), 0);
  const hasCustomerBalance = customerBalance !== undefined && customerBalance !== null;
  const rows = notes.map((n) => {
    const dt = n.dueDate ? new Date(n.dueDate).toLocaleDateString('pt-BR') : '—';
    const saleDt = n.saleDate ? new Date(n.saleDate).toLocaleDateString('pt-BR') : null;
    const jr = n.interest > 0 ? ` <span style="font-size:9px">(+juros ${fmt(n.interest)})</span>` : '';
    const partial = n.partial ? `<div style="font-size:9px;margin-left:8px">↳ PARCIAL — resta R$ ${fmt(n.outstandingAfter)} nesta nota</div>` : '';
    return `<div class="row"><span>${saleDt || dt} · ${n.saleNumber ? `Venda #${n.saleNumber}` : (n.description || 'Conta')}${jr}</span><span>R$ ${fmt(n.amount)}</span></div>${partial}`;
  }).join('');
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Recebimento</title>
    <style>
      @page { size: 80mm auto; margin: 4mm; }
      body { font-family: 'Courier New', monospace; font-size: 11px; width: 72mm; }
      h1 { font-size: 14px; text-align: center; margin: 0 0 4px; }
      .hr { border-top: 1px dashed #000; margin: 4px 0; }
      .row { display: flex; justify-content: space-between; margin: 2px 0; gap: 6px; }
      .big { font-size: 13px; font-weight: bold; }
      .warn { font-size: 13px; font-weight: bold; text-align: center; padding: 4px; border: 1px solid #000; margin-top: 4px; }
      .info { font-size: 13px; font-weight: bold; text-align: center; padding: 4px; border: 1px dashed #000; margin-top: 4px; }
      .center { text-align: center; }
    </style></head><body>
    <h1>RECEBIMENTO DE CREDIÁRIO</h1>
    <div class="center">Pagamento de ${notes.length} nota(s) a prazo${notes.some(n => n.partial) ? ' (com pagamento parcial)' : ''}</div>
    <div class="hr"></div>
    <div class="row"><span>Cliente:</span><strong style="font-size:13px">${customerName || '—'}</strong></div>
    <div class="row"><span>Forma de pagamento:</span><strong>${methodLabel}</strong></div>
    <div class="hr"></div>
    <div style="font-weight:bold;margin-bottom:2px">NOTAS QUITADAS NESTE PAGAMENTO</div>
    ${rows}
    ${totalInterest > 0 ? `<div class="row" style="margin-top:3px"><span>Juros incluídos:</span><span>R$ ${fmt(totalInterest)}</span></div>` : ''}
    <div class="hr"></div>
    <div class="row big"><span>TOTAL PAGO</span><span>R$ ${fmt(total)}</span></div>
    <div class="row"><span>Forma:</span><strong>${methodLabel}</strong></div>
    ${(() => { const q = notes.filter(n => !n.partial).length; const pnum = notes.length - q; return `<div class="warn">✓ ${q} NOTA(S) QUITADA(S)${pnum > 0 ? ` · ${pnum} PARCIAL` : ''}</div>`; })()}
    ${hasCustomerBalance
      ? (Number(customerBalance) < 0.01
          ? `<div class="info">SALDO TOTAL DO CLIENTE: R$ 0,00 (sem dívidas)</div>`
          : `<div class="info">SALDO TOTAL DO CLIENTE: R$ ${fmt(customerBalance)}</div>`)
      : ''}
    <div class="hr"></div>
    <div class="row"><span>Operador:</span><span>${operatorName || '—'}</span></div>
    <div class="row"><span>Emitido em:</span><span>${now}</span></div>
    <div class="center" style="font-size: 9px; margin-top: 6px">Smart Flow · Comprovante para o cliente</div>
  </body></html>`;
  printHtmlSilently(html);
};

export const PDVReceivablesModal = ({ open, onClose, onReceived }) => {
  const [search, setSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [receivables, setReceivables] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [bulkMethod, setBulkMethod] = useState('dinheiro');
  const [processing, setProcessing] = useState(false);
  const [viewSale, setViewSale] = useState(null); // sale para preview
  // Painel inline de pagamento parcial por linha
  const [partialPanel, setPartialPanel] = useState(null); // { receivable, amount, method }
  const [byAmount, setByAmount] = useState(null); // { amount, method, waiveInterest } — recebimento por valor global
  // Agenda automática de cobrança após pagamento parcial
  const [reschedulePanel, setReschedulePanel] = useState(null); // { receivable, outstanding, newDate }

  const fetchCustomers = useCallback(async (term) => {
    setLoading(true);
    setError('');
    try {
      const url = term ? `${API}/customers?search=${encodeURIComponent(term)}` : `${API}/customers`;
      const resp = await axios.get(url, { withCredentials: true });
      setCustomers(resp.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao buscar clientes');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReceivables = useCallback(async (customer) => {
    setLoading(true);
    setError('');
    try {
      // Busca por customer_id E customer_name para cobrir receivables sem id vinculado
      const params = new URLSearchParams({
        customer_id: customer.id,
        customer_name: customer.name,
        include_paid: 'true',
      });
      const resp = await axios.get(`${API}/accounts/receivables?${params}`, { withCredentials: true });
      setReceivables(resp.data || []);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar contas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelected(null);
      setReceivables([]);
      setSelectedIds(new Set());
      setError('');
      setSuccessMsg('');
      setBulkMethod('dinheiro');
      setViewSale(null);
      fetchCustomers('');
    }
  }, [open, fetchCustomers]);

  const handleSelectCustomer = (c) => {
    setSelected(c);
    loadReceivables(c);
  };

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const ns = new Set(prev);
      if (ns.has(id)) ns.delete(id);
      else ns.add(id);
      return ns;
    });
  };

  const pendingItems = receivables.filter(r => !r.received_at && r.status !== 'recebido');
  const paidItems = receivables.filter(r => r.received_at);

  const totalSelected = pendingItems
    .filter(r => selectedIds.has(r.id))
    .reduce((sum, r) => sum + Number(r.total_with_interest ?? r.amount ?? 0), 0);

  const toggleAllPending = () => {
    if (selectedIds.size === pendingItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingItems.map(r => r.id)));
    }
  };

  const handleViewSale = async (rec) => {
    if (!rec.sale_id) {
      setError('Esta conta não está vinculada a uma venda');
      return;
    }
    try {
      const r = await axios.get(`${API}/sales/${rec.sale_id}`, { withCredentials: true });
      setViewSale(r.data);
    } catch (err) {
      setError('Erro ao carregar venda');
    }
  };

  const handleReschedule = async () => {
    if (!reschedulePanel) return;
    const { receivable: r, newDate } = reschedulePanel;
    if (!newDate) { setError('Selecione uma nova data'); return; }
    setProcessing(true);
    setError('');
    try {
      await axios.post(
        `${API}/accounts/receivables/${r.id}/reschedule`,
        { new_due_date: new Date(newDate + 'T00:00:00').toISOString(), notes: 'Reagendado após pagamento parcial' },
        { withCredentials: true }
      );
      setSuccessMsg(`✓ Novo vencimento agendado para ${new Date(newDate + 'T00:00:00').toLocaleDateString('pt-BR')} — R$ ${reschedulePanel.outstanding.toFixed(2)}`);
      setReschedulePanel(null);
      await loadReceivables(selected);
      setTimeout(() => setSuccessMsg(''), 7000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao reagendar');
    } finally {
      setProcessing(false);
    }
  };

  const addDays = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    setReschedulePanel(p => ({ ...p, newDate: d.toISOString().substring(0, 10) }));
  };

  const openPartial = (r) => {
    const total = (r.total_with_interest ?? r.amount ?? 0) - (r.amount_paid || 0);
    setPartialPanel({
      receivable: r,
      amount: total.toFixed(2),
      method: bulkMethod,
      waiveInterest: false,
    });
    setError('');
  };

  const toggleWaiveInterest = (checked) => {
    if (!partialPanel) return;
    const r = partialPanel.receivable;
    const principalOutstanding = Math.max(0, +(((r.amount ?? 0) - (r.amount_paid || 0))).toFixed(2));
    const withInterest = Math.max(0, +(((r.total_with_interest ?? r.amount ?? 0) - (r.amount_paid || 0))).toFixed(2));
    setPartialPanel({ ...partialPanel, waiveInterest: checked, amount: (checked ? principalOutstanding : withInterest).toFixed(2) });
  };

  const handlePartialReceive = async () => {
    if (!partialPanel) return;
    const r = partialPanel.receivable;
    const value = parseFloat(partialPanel.amount);
    if (!value || value <= 0) {
      setError('Informe um valor maior que zero');
      return;
    }
    setProcessing(true);
    setError('');
    try {
      const resp = await axios.post(
        `${API}/accounts/receivables/${r.id}/receive`,
        { payment_method: partialPanel.method, amount: value, waive_interest: !!partialPanel.waiveInterest },
        { withCredentials: true }
      );
      const updated = resp.data;
      // Saldo restante vem do backend (correto mesmo após pagamentos parciais anteriores)
      const fullDue = (updated.total_with_interest ?? updated.amount ?? 0);
      const totalPaid = updated.amount_paid ?? value;
      const accountTotal = updated.amount ?? fullDue; // valor ORIGINAL da conta
      const outstandingAfter = (updated.outstanding_after !== undefined && updated.outstanding_after !== null)
        ? updated.outstanding_after
        : Math.max(0, +(fullDue - totalPaid).toFixed(2));
      // Imprime comprovante
      printReceiptSlip({
        customerName: r.customer_name,
        description: r.description,
        saleNumber: r.sale_number,
        paymentMethod: partialPanel.method,
        amountReceived: value,
        totalPaid,
        accountTotal,
        outstandingAfter,
        customerBalance: updated.customer_balance,
        dueDate: r.due_date,
        interestPaid: updated.interest || 0,
        principalPaid: value,
        operatorName: updated.last_payment_by || (updated.payments && updated.payments[updated.payments.length - 1]?.received_by),
        payments: updated.payments || [],
      });
      const msg = `✓ Recebido R$ ${value.toFixed(2)} de ${r.customer_name}. ${outstandingAfter > 0.01 ? `Restam R$ ${outstandingAfter.toFixed(2)}` : 'Conta quitada!'}`;
      setPartialPanel(null);
      if (outstandingAfter <= 0.01) {
        // Quitada: cupom já foi para a impressora — fecha tudo e volta ao PDV
        if (onReceived) onReceived({ count: 1, total: value, message: msg });
        onClose();
        return;
      }
      setSuccessMsg(msg);
      // Se sobrou saldo, abre painel de reagendamento (agenda automática)
      if (outstandingAfter > 0.01) {
        const defaultDate = new Date();
        defaultDate.setDate(defaultDate.getDate() + 7);
        setReschedulePanel({
          receivable: r,
          outstanding: outstandingAfter,
          newDate: defaultDate.toISOString().substring(0, 10),
        });
      }
      if (onReceived) onReceived({ count: 1, total: value });
      await loadReceivables(selected);
      setTimeout(() => setSuccessMsg(''), 7000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao registrar recebimento');
    } finally {
      setProcessing(false);
    }
  };

  const handleReceiveByAmount = async (plan) => {
    const amt = parseFloat(String(byAmount?.amount || '').replace(',', '.')) || 0;
    if (amt <= 0) { setError('Informe o valor que o cliente vai pagar'); return; }
    setProcessing(true);
    setError('');
    setSuccessMsg('');
    try {
      const { data } = await axios.post(
        `${API}/accounts/receivables/receive-by-amount`,
        { customer_id: selected.id, amount: amt, payment_method: byAmount.method, waive_interest: !!byAmount.waiveInterest },
        { withCredentials: true }
      );
      const notes = (data.applied || []).map(a => ({
        saleNumber: a.sale_number,
        description: a.description,
        dueDate: a.due_date,
        saleDate: a.created_at,
        amount: a.amount_received,
        interest: a.interest_paid,
        partial: !a.fully_paid,
        outstandingAfter: a.outstanding_after,
      }));
      printBulkReceiptSlip({
        customerName: selected?.name,
        paymentMethod: data.payment_method,
        notes,
        customerBalance: data.customer_balance,
        operatorName: data.received_by,
      });
      const partialTxt = data.notes_partially_paid > 0 ? ` · ${data.notes_partially_paid} parcial` : '';
      const msg = `✓ R$ ${Number(data.amount_applied).toFixed(2)} recebido de ${selected.name} em ${PAYMENT_METHODS.find(p => p.value === data.payment_method)?.label}: ${data.notes_fully_paid} nota(s) quitada(s)${partialTxt}`;
      setByAmount(null);
      // Cupom já foi para a impressora — fecha tudo e volta ao PDV
      if (onReceived) onReceived({ count: (data.applied || []).length, total: data.amount_applied, message: msg });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao registrar recebimento');
    } finally {
      setProcessing(false);
    }
  };

  const handleReceiveSelected = async () => {
    if (selectedIds.size === 0) {
      setError('Selecione pelo menos uma venda');
      return;
    }
    setProcessing(true);
    setError('');
    setSuccessMsg('');
    let okCount = 0;
    let failed = [];
    const paidNotes = [];
    let lastResp = null;
    const methodUsed = bulkMethod; // forma escolhida pelo operador — vale para todas as notas deste atendimento
    for (const id of selectedIds) {
      const rec = receivables.find(r => r.id === id);
      try {
        const resp = await axios.post(
          `${API}/accounts/receivables/${id}/receive`,
          { payment_method: methodUsed },
          { withCredentials: true }
        );
        okCount++;
        const updated = resp.data || {};
        lastResp = updated;
        const paidNow = updated.last_payment_amount ?? updated.total_with_interest ?? updated.amount ?? rec?.total_with_interest ?? rec?.amount ?? 0;
        paidNotes.push({
          saleNumber: updated.sale_number ?? rec?.sale_number,
          description: updated.description ?? rec?.description,
          dueDate: updated.due_date ?? rec?.due_date,
          saleDate: updated.created_at ?? rec?.created_at,
          amount: paidNow,
          interest: updated.interest || 0,
        });
      } catch (err) {
        failed.push(rec?.description || id);
      }
    }
    // UM comprovante com TODAS as notas quitadas neste atendimento
    if (paidNotes.length > 0) {
      printBulkReceiptSlip({
        customerName: selected?.name || paidNotes[0]?.customerName,
        paymentMethod: methodUsed,
        notes: paidNotes,
        customerBalance: lastResp?.customer_balance,
        operatorName: lastResp?.payments?.[lastResp.payments.length - 1]?.received_by,
      });
    }
    setProcessing(false);

    const msg = `✓ ${okCount} venda(s) recebida(s) para ${selected.name} · Total: R$ ${totalSelected.toFixed(2)} em ${PAYMENT_METHODS.find(p => p.value === bulkMethod)?.label}`;
    if (okCount > 0 && failed.length === 0) {
      // Cupom já foi para a impressora — fecha tudo e volta ao PDV
      if (onReceived) onReceived({ count: okCount, total: totalSelected, message: msg });
      onClose();
      return;
    }
    if (okCount > 0) {
      setSuccessMsg(msg);
      if (onReceived) onReceived({ count: okCount, total: totalSelected });
      await loadReceivables(selected);
      setTimeout(() => setSuccessMsg(''), 6000);
    }
    if (failed.length > 0) {
      setError(`Falha em ${failed.length}: ${failed.slice(0, 3).join(', ')}`);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="pdv-receivables-modal"
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <Wallet className="text-emerald-600" size={26} />
            <div>
              <h3 className="text-xl font-bold text-slate-900">Recebimentos de Clientes</h3>
              <p className="text-xs text-slate-500">Selecione múltiplas vendas e receba de uma vez · entra no caixa atual</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
            data-testid="pdv-receivables-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          {successMsg && (
            <Alert className="border-emerald-300 bg-emerald-50 text-emerald-900">
              <AlertDescription className="font-medium">{successMsg}</AlertDescription>
            </Alert>
          )}

          {/* Busca de cliente */}
          {!selected && (
            <div>
              <Label className="text-xs uppercase tracking-wider">Buscar cliente por NOME, CPF ou telefone</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchCustomers(search)}
                    placeholder="Digite o nome do cliente..."
                    className="pl-9"
                    data-testid="pdv-receivables-search"
                  />
                </div>
                <Button onClick={() => fetchCustomers(search)} variant="outline">Buscar</Button>
              </div>
              <div className="mt-3 border border-slate-200 rounded-md overflow-hidden max-h-80 overflow-y-auto">
                {loading ? (
                  <p className="text-center text-slate-500 p-6 text-sm">Carregando...</p>
                ) : customers.length === 0 ? (
                  <p className="text-center text-slate-500 p-6 text-sm">Nenhum cliente encontrado</p>
                ) : (
                  <div className="divide-y divide-slate-200">
                    {customers.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => handleSelectCustomer(c)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSelectCustomer(c)}
                        className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors flex items-center justify-between"
                        data-testid={`customer-row-${c.id}`}
                      >
                        <div>
                          <p className="font-medium text-slate-900">{c.name}</p>
                          <p className="text-xs text-slate-500">
                            {c.cpf_cnpj && `CPF/CNPJ: ${c.cpf_cnpj} · `}
                            {c.phone && `Fone: ${c.phone}`}
                          </p>
                        </div>
                        {Number(c.balance) > 0 && (
                          <span className="text-xs font-bold text-orange-700 bg-orange-100 px-2 py-1 rounded">
                            Devendo: R$ {Number(c.balance).toFixed(2)}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Cliente selecionado + recebíveis (multi-seleção) */}
          {selected && (
            <div>
              <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-md p-3 mb-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-emerald-700 font-bold">Cliente selecionado</p>
                  <p className="font-bold text-slate-900 text-lg">{selected.name}</p>
                  <p className="text-xs text-slate-600">{selected.cpf_cnpj || ''} {selected.phone ? `· ${selected.phone}` : ''}</p>
                </div>
                <Button variant="outline" onClick={() => { setSelected(null); setReceivables([]); setSelectedIds(new Set()); }} data-testid="pdv-receivables-change-customer">
                  <ChevronLeft size={14} className="mr-1" /> Trocar cliente
                </Button>
              </div>

              {loading ? (
                <p className="text-center text-slate-500 p-6 text-sm">Carregando vendas do cliente...</p>
              ) : receivables.length === 0 ? (
                <div className="text-center p-8 bg-slate-50 border border-slate-200 rounded-md">
                  <p className="font-medium text-slate-700">Este cliente ainda não tem vendas a prazo registradas</p>
                  <p className="text-xs text-slate-500 mt-1">As vendas feitas no PDV com &quot;Venda a Prazo&quot; aparecerão aqui automaticamente.</p>
                </div>
              ) : (
                <>
                  {/* PENDENTES */}
                  {pendingItems.length > 0 && (
                    <>
                      <div className="flex items-center justify-between bg-amber-50 px-3 py-2 rounded-t-md border border-amber-200" data-testid="pending-section">
                        <label className="flex items-center gap-2 text-xs font-bold text-amber-800 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedIds.size > 0 && selectedIds.size === pendingItems.length}
                            onChange={toggleAllPending}
                            className="w-4 h-4"
                            data-testid="receivables-toggle-all"
                          />
                          Vendas a receber ({pendingItems.length})
                        </label>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => { setByAmount(byAmount ? null : { amount: '', method: bulkMethod, waiveInterest: false }); setSelectedIds(new Set()); setPartialPanel(null); setError(''); }}
                            className={`text-xs font-bold px-2 py-1 rounded border ${byAmount ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'}`}
                            data-testid="receive-by-amount-toggle"
                          >
                            Receber por valor total
                          </button>
                          <span className="text-xs text-amber-800">
                            {selectedIds.size} selecionada(s)
                          </span>
                        </div>
                      </div>

                      {byAmount && (() => {
                        const amt = parseFloat(String(byAmount.amount).replace(',', '.')) || 0;
                        const ordered = [...pendingItems].sort((a, b) => new Date(a.due_date || a.created_at || 0) - new Date(b.due_date || b.created_at || 0) || new Date(a.created_at || 0) - new Date(b.created_at || 0));
                        const dueOf = (r) => byAmount.waiveInterest ? Math.max(0, (r.amount || 0) - (r.amount_paid || 0)) : (r.total_with_interest ?? r.amount ?? 0);
                        const totalDue = ordered.reduce((s, r) => s + dueOf(r), 0);
                        let rem = amt;
                        const plan = ordered.map((r) => { const d = dueOf(r); const pay = Math.min(rem, d); rem = Math.max(0, +(rem - pay).toFixed(2)); return { r, due: d, pay: +pay.toFixed(2), full: pay >= d - 0.005 }; }).filter(x => x.pay > 0.004);
                        const over = amt > totalDue + 0.01;
                        return (
                          <div className="border border-blue-300 bg-blue-50 rounded-md p-3 my-2" data-testid="receive-by-amount-panel">
                            <p className="text-xs font-bold text-blue-800 uppercase tracking-wide mb-2">Recebimento por valor total · quita da nota mais antiga para a mais nova</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                              <div>
                                <label className="text-[11px] text-slate-600 font-bold">Valor que o cliente vai pagar (R$)</label>
                                <input
                                  type="number" step="0.01" min="0" autoFocus
                                  value={byAmount.amount}
                                  onChange={(e) => setByAmount({ ...byAmount, amount: e.target.value })}
                                  onKeyDown={(e) => { if (e.key === 'Enter' && amt > 0 && !over && !processing) { e.preventDefault(); handleReceiveByAmount(plan); } }}
                                  className="w-full h-11 border border-blue-300 rounded-md px-3 text-xl font-black text-right"
                                  placeholder="0,00"
                                  data-testid="receive-by-amount-input"
                                />
                              </div>
                              <div>
                                <label className="text-[11px] text-slate-600 font-bold">Forma de pagamento</label>
                                <select value={byAmount.method} onChange={(e) => setByAmount({ ...byAmount, method: e.target.value })} className="w-full h-11 border border-blue-300 rounded-md px-2 text-sm bg-white" data-testid="receive-by-amount-method">
                                  {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                </select>
                              </div>
                              <Button
                                onClick={() => handleReceiveByAmount(plan)}
                                disabled={processing || amt <= 0 || over}
                                className="h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold"
                                data-testid="receive-by-amount-confirm"
                              >
                                {processing ? 'Processando...' : `Receber R$ ${amt.toFixed(2)}`}
                              </Button>
                            </div>
                            <label className="flex items-center gap-2 mt-2 text-xs text-slate-700 cursor-pointer select-none">
                              <input type="checkbox" checked={byAmount.waiveInterest} onChange={(e) => setByAmount({ ...byAmount, waiveInterest: e.target.checked })} className="w-4 h-4" data-testid="receive-by-amount-waive" />
                              Receber sem juros
                            </label>
                            <div className="mt-2 text-xs text-slate-700">
                              Dívida total do cliente: <b>R$ {totalDue.toFixed(2)}</b>
                              {over && <span className="ml-2 text-red-700 font-bold" data-testid="receive-by-amount-over">Valor maior que a dívida — o cliente não pode ficar com crédito sobrando. Máximo: R$ {totalDue.toFixed(2)}</span>}
                            </div>
                            {amt > 0 && !over && plan.length > 0 && (
                              <div className="mt-2 bg-white border border-blue-200 rounded-md divide-y divide-slate-100 max-h-40 overflow-y-auto" data-testid="receive-by-amount-preview">
                                {plan.map(({ r, due, pay, full }) => (
                                  <div key={r.id} className="flex items-center justify-between px-3 py-1.5 text-xs">
                                    <span>
                                      {r.sale_number ? <b className="text-blue-700 mr-1">Venda #{r.sale_number}</b> : null}
                                      <span className="text-slate-500">venc. {r.due_date ? new Date(r.due_date).toLocaleDateString('pt-BR') : '—'} · devido R$ {due.toFixed(2)}</span>
                                    </span>
                                    <span className={full ? 'text-emerald-700 font-bold' : 'text-amber-700 font-bold'}>
                                      {full ? 'QUITA' : `PARCIAL (resta R$ ${(due - pay).toFixed(2)})`} · R$ {pay.toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      <div className="border border-t-0 border-amber-200 rounded-b-md divide-y divide-slate-200 max-h-60 overflow-y-auto">
                        {pendingItems.map((r) => {
                          const isOverdue = (r.days_overdue || 0) > 0;
                          const total = r.total_with_interest ?? r.amount ?? 0;
                          const interest = r.interest || 0;
                          const due = r.due_date ? new Date(r.due_date).toLocaleDateString('pt-BR') : '—';
                          const checked = selectedIds.has(r.id);
                          const saleN = r.sale_number ? `#${r.sale_number}` : '';
                          return (
                            <div
                              key={r.id}
                              className={`p-3 ${checked ? 'bg-blue-50' : isOverdue ? 'bg-red-50' : 'bg-white'}`}
                              data-testid={`receivable-row-${r.id}`}
                            >
                              <div className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleSelected(r.id)}
                                  className="w-4 h-4 mt-1"
                                  data-testid={`receivable-check-${r.id}`}
                                />
                                <div className="flex-1">
                                  <p className="font-medium text-slate-900">
                                    {saleN && <span className="text-blue-700 font-bold mr-1">Venda {saleN}</span>}
                                    {r.description || 'Conta a receber'}
                                  </p>
                                  <div className="flex flex-wrap gap-3 text-xs text-slate-600 mt-1">
                                    <span>Venc.: <strong>{due}</strong></span>
                                    <span>Valor: <strong>R$ {Number(r.amount).toFixed(2)}</strong></span>
                                    {interest > 0 && (
                                      <span className="text-red-700">+ juros R$ {Number(interest).toFixed(2)}</span>
                                    )}
                                    {isOverdue ? (
                                      <span className="text-red-700 font-bold flex items-center gap-1">
                                        <AlertTriangle size={12} /> {r.days_overdue} dia(s) atrasado
                                      </span>
                                    ) : (
                                      <span className="text-emerald-700 font-medium">no prazo</span>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className={`text-xl font-black ${isOverdue ? 'text-red-700' : 'text-emerald-700'}`}>
                                    R$ {Number(total).toFixed(2)}
                                  </p>
                                  {(r.amount_paid || 0) > 0 && (
                                    <p className="text-[10px] text-amber-700">Já pago: R$ {Number(r.amount_paid).toFixed(2)}</p>
                                  )}
                                  <div className="flex flex-col gap-1 items-end mt-1">
                                    <Button
                                      size="sm"
                                      onClick={() => openPartial(r)}
                                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-2 text-xs"
                                      data-testid={`receivable-partial-${r.id}`}
                                    >
                                      💰 Receber
                                    </Button>
                                    {r.sale_id && (
                                      <button
                                        onClick={() => handleViewSale(r)}
                                        className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                                        data-testid={`view-sale-${r.id}`}
                                      >
                                        <Eye size={11} /> Ver itens
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {partialPanel && partialPanel.receivable.id === r.id && (
                                <div className="mt-3 bg-emerald-50 border border-emerald-300 rounded p-3 space-y-2" data-testid={`partial-panel-${r.id}`}>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                                    <div>
                                      <Label className="text-xs">Valor a receber (R$)</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        value={partialPanel.amount}
                                        onChange={(e) => setPartialPanel({ ...partialPanel, amount: e.target.value })}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') { e.preventDefault(); handlePartialReceive(); }
                                          if (e.key === 'Escape') { e.preventDefault(); setPartialPanel(null); }
                                          if (e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            document.querySelector(`[data-testid="partial-method-${r.id}"]`)?.focus();
                                          }
                                        }}
                                        autoFocus
                                        data-testid={`partial-amount-${r.id}`}
                                      />
                                      <p className="text-[10px] text-slate-500 mt-1">
                                        Total da conta: R$ {(total - (r.amount_paid || 0)).toFixed(2)}. Digite um valor menor para <strong>pagamento parcial</strong>.
                                        {(() => {
                                          const maxVal = total - (r.amount_paid || 0);
                                          const typed = parseFloat(partialPanel.amount) || 0;
                                          const willRemain = Math.max(0, +(maxVal - typed).toFixed(2));
                                          return willRemain > 0.01 && typed > 0
                                            ? <span className="block text-amber-700 font-bold mt-0.5">Ficará em aberto: R$ {willRemain.toFixed(2)}</span>
                                            : null;
                                        })()}
                                      </p>
                                    </div>
                                    <div>
                                      <Label className="text-xs">Forma de pagamento</Label>
                                      <select
                                        value={partialPanel.method}
                                        onChange={(e) => setPartialPanel({ ...partialPanel, method: e.target.value })}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') { e.preventDefault(); handlePartialReceive(); }
                                        }}
                                        className="w-full text-sm border border-slate-300 rounded px-2 h-9"
                                        data-testid={`partial-method-${r.id}`}
                                      >
                                        {PAYMENT_METHODS.map(p => (
                                          <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <div className="flex gap-1">
                                      <Button
                                        variant="outline"
                                        onClick={() => setPartialPanel(null)}
                                        className="h-9 text-xs"
                                        data-testid={`partial-cancel-${r.id}`}
                                      >
                                        Cancelar
                                      </Button>
                                      <Button
                                        onClick={handlePartialReceive}
                                        disabled={processing}
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-xs font-bold flex-1"
                                        data-testid={`partial-confirm-${r.id}`}
                                      >
                                        <Printer size={14} className="mr-1" /> Receber + Imprimir
                                      </Button>
                                    </div>
                                  </div>
                                  {interest > 0 && (
                                    <label className="flex items-center gap-2 mt-2 cursor-pointer select-none" data-testid={`partial-waive-label-${r.id}`}>
                                      <input
                                        type="checkbox"
                                        checked={!!partialPanel.waiveInterest}
                                        onChange={(e) => toggleWaiveInterest(e.target.checked)}
                                        className="w-4 h-4"
                                        data-testid={`partial-waive-${r.id}`}
                                      />
                                      <span className="text-xs text-slate-700">
                                        Receber <strong>sem juros</strong> (não cobrar os R$ {Number(interest).toFixed(2)} de juros)
                                      </span>
                                    </label>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* PAGAS (somente leitura) */}
                  {paidItems.length > 0 && (
                    <div className="mt-4" data-testid="paid-section">
                      <div className="bg-emerald-50 px-3 py-2 rounded-t-md border border-emerald-200">
                        <p className="text-xs font-bold text-emerald-800">Vendas já pagas ({paidItems.length})</p>
                      </div>
                      <div className="border border-t-0 border-emerald-200 rounded-b-md divide-y divide-slate-200 max-h-48 overflow-y-auto">
                        {paidItems.map((r) => {
                          const saleN = r.sale_number ? `#${r.sale_number}` : '';
                          const recDate = r.received_at ? new Date(r.received_at).toLocaleDateString('pt-BR') : '—';
                          return (
                            <div key={r.id} className="p-2 bg-emerald-50/40 text-sm flex items-center justify-between" data-testid={`paid-row-${r.id}`}>
                              <div className="flex items-center gap-2">
                                <Check size={14} className="text-emerald-600" />
                                <span className="font-medium">{saleN && <span className="text-blue-700 mr-1">Venda {saleN}</span>}{r.description}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-slate-500">Pago em {recDate} ({r.payment_method || '—'})</span>
                                <span className="font-bold text-emerald-700">R$ {Number(r.amount).toFixed(2)}</span>
                                {r.sale_id && (
                                  <button
                                    onClick={() => handleViewSale(r)}
                                    className="text-xs text-blue-600 hover:underline"
                                    title="Ver itens"
                                    data-testid={`view-sale-paid-${r.id}`}
                                  >
                                    <Eye size={12} />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Painel de recebimento em massa */}
                  {selectedIds.size > 0 && (
                    <div className="mt-3 bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-300 rounded-md p-3" data-testid="bulk-receive-panel">
                      <p className="text-xs uppercase tracking-wider text-emerald-700 font-bold mb-2">
                        {selectedIds.size} venda(s) selecionada(s)
                      </p>
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <Label className="text-xs">Total a receber</Label>
                          <p className="text-3xl font-black text-emerald-700" data-testid="bulk-receive-total">R$ {totalSelected.toFixed(2)}</p>
                        </div>
                        <div className="flex-1 min-w-[140px]">
                          <Label className="text-xs">Forma de pagamento</Label>
                          <select
                            value={bulkMethod}
                            onChange={(e) => setBulkMethod(e.target.value)}
                            className="w-full text-sm border border-slate-300 rounded px-2 py-2 mt-1"
                            data-testid="bulk-receive-method"
                          >
                            {PAYMENT_METHODS.map(p => (
                              <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                          </select>
                        </div>
                        <Button
                          onClick={handleReceiveSelected}
                          disabled={processing}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-6 font-bold"
                          data-testid="bulk-receive-confirm"
                        >
                          {processing ? 'Processando...' : (
                            <><Check size={16} className="mr-2" /> Receber R$ {totalSelected.toFixed(2)}</>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-200 flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="pdv-receivables-footer-close">Fechar</Button>
        </div>
      </div>

      {/* Modal de visualização da venda */}
      {viewSale && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4" {...overlayClose(() => setViewSale(null))}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="view-sale-modal">
            <h3 className="text-xl font-bold mb-3">Venda #{viewSale.sale_number}</h3>
            <div className="text-sm space-y-1 mb-3">
              <p><strong>Data:</strong> {new Date(viewSale.created_at).toLocaleString('pt-BR')}</p>
              <p><strong>Operador:</strong> {viewSale.cashier_name || '—'}</p>
              <p><strong>Cliente:</strong> {viewSale.customer_name || '—'}</p>
            </div>
            <div className="border-t pt-2">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-bold">Itens</p>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {(viewSale.items || []).map((it, i) => (
                  <div key={i} className={`flex justify-between text-sm ${it.cancelled ? 'line-through text-slate-400' : ''}`}>
                    <span>{it.quantity}x {it.product_name}</span>
                    <span className="font-bold">R$ {Number(it.subtotal).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t pt-2 mt-2 flex justify-between font-bold text-lg">
              <span>TOTAL</span>
              <span className="text-blue-700">R$ {Number(viewSale.total).toFixed(2)}</span>
            </div>
            <Button onClick={() => setViewSale(null)} className="w-full mt-4">Fechar</Button>
          </div>
        </div>
      )}
      {/* Modal: Agenda automática de cobrança após pagamento parcial */}
      {reschedulePanel && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60" data-testid="reschedule-modal" onClick={(e) => e.stopPropagation()}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 m-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">📅</span>
              <h3 className="text-xl font-bold text-slate-900">Agendar próxima cobrança</h3>
            </div>
            <p className="text-sm text-slate-600 mb-1">
              <strong>{reschedulePanel.receivable.customer_name}</strong>
            </p>
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-4">
              Restam <strong>R$ {reschedulePanel.outstanding.toFixed(2)}</strong> em aberto. Quando você quer receber o restante?
            </p>

            <div className="grid grid-cols-4 gap-2 mb-3">
              {[
                { d: 7, label: '+7 dias' },
                { d: 15, label: '+15 dias' },
                { d: 30, label: '+30 dias' },
                { d: 60, label: '+60 dias' },
              ].map(opt => (
                <Button
                  key={opt.d}
                  variant="outline"
                  onClick={() => addDays(opt.d)}
                  className="text-xs h-9"
                  data-testid={`reschedule-${opt.d}d`}
                >
                  {opt.label}
                </Button>
              ))}
            </div>

            <div className="mb-4">
              <Label className="text-xs">Ou escolha a data exata</Label>
              <Input
                type="date"
                value={reschedulePanel.newDate}
                min={new Date().toISOString().substring(0, 10)}
                onChange={(e) => setReschedulePanel(p => ({ ...p, newDate: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); handleReschedule(); }
                  if (e.key === 'Escape') { e.preventDefault(); setReschedulePanel(null); }
                }}
                autoFocus
                data-testid="reschedule-date"
              />
              {reschedulePanel.newDate && (
                <p className="text-xs text-emerald-700 mt-1">
                  ✓ Nova cobrança em: <strong>{new Date(reschedulePanel.newDate + 'T00:00:00').toLocaleDateString('pt-BR')}</strong>
                </p>
              )}
            </div>

            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setReschedulePanel(null)}
                className="flex-1"
                data-testid="reschedule-skip"
              >
                Pular (manter data atual)
              </Button>
              <Button
                onClick={handleReschedule}
                disabled={processing}
                className="bg-blue-600 hover:bg-blue-700 text-white flex-1 font-bold"
                data-testid="reschedule-confirm"
              >
                📅 Reagendar
              </Button>
            </div>

            <p className="text-[10px] text-slate-500 mt-3 text-center">
              💡 Reagendar aumenta a chance do cliente lembrar e pagar.
            </p>
          </div>
        </div>
      )}

    </div>
  );
};
